import { twMerge } from 'tailwind-merge';

import { Container } from 'components/GridV2';

import { IQueryParams, ISanityCompareTiers } from 'typescriptDefs';

import { useIsMobileSize } from 'helpers';

import Tabs from '../tabs/Tabs';
import TabsContent from '../tabs/TabsContent';
import TabsList from '../tabs/TabsList';
import TabsTrigger from '../tabs/TabsTrigger';
import TiersTableContent from './TiersTableContent';

interface TiersCompareTableProps {
  category?: string;
  className?: string;
  data: ISanityCompareTiers;
  queryParams?: IQueryParams;
  toggleBenefits?: boolean;
}

const TiersCompareTable = ({ data, className, toggleBenefits, queryParams, category }: TiersCompareTableProps) => {
  const isMobile = useIsMobileSize();

  return (
    <Container className={twMerge('px-0 xs:px-32', className)}>
      {isMobile ? (
        // Mobile view - Show tier filter tabs
        <div className="px-18">
          <Tabs defaultTab={0} contextId="tier-filter">
            <TabsList className="mb-16 flex w-full items-center justify-between" spanClassName="w-[calc(33.333%-6px)]">
              {data.tiers.map((tier, index) => (
                <TabsTrigger
                  key={tier.variant}
                  value={index}
                  contextId="tier-filter"
                  className={twMerge(
                    'mx-1 flex flex-1 items-center justify-center first:ml-0 last:mr-0 [&.active-tab~.tab-toggle]:translate-x-[calc(200%+8px)]',
                    index === 0 && '[&.active-tab~.tab-toggle]:translate-x-4',
                    index === 1 && '[&.active-tab~.tab-toggle]:translate-x-[calc(100%+4px)]',
                  )}
                >
                  {tier.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {data.tiers.map((tier, index) => (
              <TabsContent key={tier.variant} value={index} contextId="tier-filter">
                <Tabs defaultTab={0} contextId={`monthly-yearly-${tier.variant}`}>
                  <TabsList className="hidden">
                    <TabsTrigger value={0} contextId={`monthly-yearly-${tier.variant}`}>
                      {data.tabLabelMonthly}
                    </TabsTrigger>
                    <TabsTrigger value={1} contextId={`monthly-yearly-${tier.variant}`}>
                      {data.tabLabelYearly}
                    </TabsTrigger>
                  </TabsList>
                  <TiersTableContent
                    category={category}
                    footerNotes={data.footerNotes}
                    selectedTiers={data.tiers.filter((t) => t.variant === tier.variant)}
                    tabLabelYearly={data.tabLabelYearly}
                    tableHeader={data.tableHeader}
                    tiers={data.tiers}
                    toggleBenefits={toggleBenefits}
                    queryParams={queryParams}
                  />
                </Tabs>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      ) : (
        // Desktop view - Show only monthly/yearly tabs and all tiers
        <Tabs defaultTab={0} contextId="monthly-yearly-desktop">
          <TabsList className="max-w-[206px]" spanClassName="w-[calc(50%-3px)]">
            <TabsTrigger
              value={0}
              contextId="monthly-yearly-desktop"
              className="[&.active-tab~.tab-toggle]:translate-x-4"
            >
              {data.tabLabelMonthly}
            </TabsTrigger>
            <TabsTrigger
              value={1}
              contextId="monthly-yearly-desktop"
              className="[&.active-tab~.tab-toggle]:translate-x-[calc(100%+4px)]"
            >
              {data.tabLabelYearly}
            </TabsTrigger>
          </TabsList>
          <TiersTableContent
            category={category}
            footerNotes={data.footerNotes}
            selectedTiers={data.tiers}
            tabLabelYearly={data.tabLabelYearly}
            tableHeader={data.tableHeader}
            tiers={data.tiers}
            toggleBenefits={toggleBenefits}
            queryParams={queryParams}
          />
        </Tabs>
      )}
    </Container>
  );
};

export default TiersCompareTable;
